import { getExernal, putJSONExernal } from '@/service/external';

/** ModelScope 任务类型 */
export interface ModelScopeTask {
  ChineseName: string;
  Name: string;
  [key: string]: any;
}

/** ModelScope 组织信息 */
export interface ModelScopeOrganization {
  Avatar: string;
  FullName: string;
  Name: string;
  Path: string;
  [key: string]: any;
}

/** ModelScope 模型项类型 */
export interface ModelScopeModelItem {
  Id: number;
  Path: string;
  Name: string;
  License: string;
  Tasks: ModelScopeTask[];
  Tags: string[];
  Organization: ModelScopeOrganization;
  LastUpdatedTime: number;
  StorageSize: number;
  Avatar: string;
  [key: string]: any;
}

/** ModelScope 外部接口响应类型 */
export interface ModelScopeResponse {
  code?: number;
  Code?: number;
  Data: {
    FiledAgg: any;
    ModelCollection?: ModelScopeModelItem[];
    Model?: {
      Models?: ModelScopeModelItem[];
      TotalCount?: number;
    };
  };
  data?: {
    FiledAgg?: any;
    ModelCollection?: ModelScopeModelItem[];
    Model?: {
      Models?: ModelScopeModelItem[];
      TotalCount?: number;
    };
  };
  [key: string]: any;
}

/** ModelScope Skill 一级分类 */
export interface ModelScopeSkillCatalog {
  Business?: string;
  CatalogID?: string;
  ChineseName?: string;
  ID?: number;
  Name?: string;
  Parent?: number;
  Weight?: number;
  [key: string]: any;
}

/** ModelScope Skill 项类型 */
export interface ModelScopeSkillItem {
  Path: string;
  Name: string;
  DisplayName: string;
  Description: string;
  DescriptionEn?: string;
  License: string;
  Visits: number;
  DownloadCount: number;
  SourceAvatar?: string;
  SourceDeveloper?: string;
  Owner?: string;
  Tags?: string[];
  L1?: ModelScopeSkillCatalog | null;
  L2?: ModelScopeSkillCatalog | null;
  Organization?: ModelScopeOrganization;
  [key: string]: any;
}

/** ModelScope Skill 列表接口响应类型 */
export interface ModelScopeSkillResponse {
  code?: number;
  Code?: number;
  Data: {
    SkillList?: ModelScopeSkillItem[];
    TotalCount?: number;
  };
  Message?: string;
  Success?: boolean;
  [key: string]: any;
}

/** ModelScope 数据集标签 */
export interface ModelScopeDatasetTag {
  domain?: string;
  task?: string;
  filter?: string;
  label?: string;
  level1Tag?: string;
  level2Tag?: string;
  level2TagName?: string;
  level3Tag?: string;
  [key: string]: any;
}

/** ModelScope 数据集项 */
export interface ModelScopeDatasetItem {
  Id: number;
  Namespace: string;
  Name: string;
  ChineseName?: string;
  License?: string;
  Description?: string;
  Owner?: string;
  UserDefineTags?: string;
  Likes?: number;
  Downloads?: number;
  StorageSize?: number;
  GmtCreate?: number;
  Tags?: ModelScopeDatasetTag[] | null;
  FullName?: string;
  Avatar?: string;
  NickName?: string;
  Organization?: {
    Name?: string;
    FullName?: string;
    Avatar?: string;
    [key: string]: any;
  } | null;
  ApprovalMode?: number;
  [key: string]: any;
}

/** ModelScope 数据集列表接口响应 */
export interface ModelScopeDatasetResponse {
  Code?: number;
  Data?: ModelScopeDatasetItem[];
  TotalCount?: number;
  PageNumber?: number;
  PageSize?: number;
  Message?: string;
  [key: string]: any;
}

/** ModelScope 数据集详情接口响应 */
export interface ModelScopeDatasetDetailResponse {
  Code?: number;
  Data?: ModelScopeDatasetItem;
  Message?: string;
  [key: string]: any;
}

/** 从 ModelScope 获取模型列表 */
export async function getModelScopeModels(params: { PageSize: number; PageNumber: number; Name?: string }): Promise<ModelScopeResponse> {
  // 使用独立的 axios 实例直接请求第三方 API，不携带平台认证信息
  const path = '/modelscope-api/v1/dolphin/models';
  return putJSONExernal(path, params);
}

/** 从 ModelScope 获取 Skill 列表 */
export async function getModelScopeSkills(params: {
  PageSize: number;
  PageNumber: number;
  Query?: string;
}): Promise<ModelScopeSkillResponse> {
  const path = '/modelscope-api/v1/dolphin/skills';
  return putJSONExernal(path, {
    PageSize: params.PageSize,
    PageNumber: params.PageNumber,
    Query: params.Query ?? '',
    Sort: 'Default',
    Criterion: [],
    WithTopCollection: false,
  });
}

/** 从 ModelScope 获取数据集列表 */
export async function getModelScopeDatasets(params: {
  PageSize: number;
  PageNumber: number;
  Query?: string;
}): Promise<ModelScopeDatasetResponse> {
  const path = '/modelscope-api/v1/dolphin/datasets';
  return getExernal(path, {
    PageSize: params.PageSize,
    PageNumber: params.PageNumber,
    Target: '',
    Query: params.Query ?? '',
  });
}

/** 从 ModelScope 获取数据集详情（含 StorageSize） */
export async function getModelScopeDatasetDetail(namespace: string, name: string): Promise<ModelScopeDatasetDetailResponse> {
  const path = `/modelscope-api/v1/datasets/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}`;
  return getExernal(path);
}
